import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeContext';

/** One stacked series across the month axis. `values` is aligned index-for-index
 *  with the `months` prop. */
export type CreditSeries = { key: string; label: string; values: number[] };

type Props = {
  /** Month bucket starts, unix *seconds*, oldest → newest. */
  months: number[];
  /** One or more series. A single series renders as plain (unstacked) bars with no
   *  legend — the surrounding title names it. */
  series: CreditSeries[];
  /** Height of the plotting area in px (bars only, excludes axis/legend). */
  height?: number;
  /** Custom detail panel for the clicked month. When omitted, a built-in
   *  per-series breakdown for that month is shown instead. */
  renderDetail?: (monthIndex: number) => React.ReactNode;
};

// Categorical palette in FIXED order (validated for both surfaces via the dataviz
// skill's checker). Never cycled: a series past the last slot falls to the neutral
// "overflow" colour — callers fold long tails into a single "Other" series before
// they ever reach that point.
const PALETTE_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#4a3aa7'];
const PALETTE_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#9085e9'];
const OVERFLOW_LIGHT = '#9ca3af';
const OVERFLOW_DARK = '#6b7280';

/** Hand-rolled stacked monthly bar chart — magnitude over time. No chart library:
 *  the app ships neither recharts nor d3, and a stacked bar is a handful of divs.
 *  Bars anchor to the baseline with a 2px surface gap between stacked segments
 *  (the relief the light palette needs), a legend for ≥2 series, a hover tooltip
 *  per segment, and click-to-open a month detail panel. Theme-aware: the palette
 *  flips with the resolved theme. */
export function MonthlyCreditsBars({ months, series, height = 180, renderDetail }: Props) {
  const { t, i18n } = useTranslation();
  const { resolved } = useTheme();
  const palette = resolved === 'dark' ? PALETTE_DARK : PALETTE_LIGHT;
  const overflow = resolved === 'dark' ? OVERFLOW_DARK : OVERFLOW_LIGHT;
  const [hover, setHover] = useState<{ m: number; s: number } | null>(null);
  const [selected, setSelected] = useState<number | null>(null);

  const monthFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { month: 'short' }),
    [i18n.language],
  );
  const monthYearFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' }),
    [i18n.language],
  );
  const compact = useMemo(
    () => new Intl.NumberFormat(i18n.language, { notation: 'compact', maximumFractionDigits: 1 }),
    [i18n.language],
  );
  const full = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const colorOf = (i: number) => (i < palette.length ? palette[i] : overflow);

  const totals = months.map((_, mi) => series.reduce((sum, s) => sum + (s.values[mi] ?? 0), 0));
  const max = Math.max(0, ...totals);
  // Topmost visible segment per month — the highest-index series with a positive
  // value (flex-col-reverse puts series[last] on top). Only that segment is rounded.
  const topSeriesByMonth = months.map((_, mi) => {
    let top = -1;
    series.forEach((s, si) => {
      if ((s.values[mi] ?? 0) > 0) top = si;
    });
    return top;
  });

  if (max <= 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">{t('subscription.noUsage')}</p>
    );
  }

  const hoveredSeries = hover ? series[hover.s] : undefined;
  const hoveredMonth = hover ? months[hover.m] : undefined;
  const hovered =
    hoveredSeries && hoveredMonth != null && hover
      ? {
          month: monthFmt.format(new Date(hoveredMonth * 1000)),
          label: hoveredSeries.label,
          value: hoveredSeries.values[hover.m] ?? 0,
        }
      : null;

  const selUnix = selected != null ? months[selected] : undefined;

  return (
    <div className="space-y-3">
      <div className="relative">
        {/* Tooltip — floats above the bars; identity comes from the label, not colour alone. */}
        {hovered ? (
          <div className="pointer-events-none absolute left-1/2 top-0 z-10 -translate-x-1/2 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs shadow-md">
            <span className="text-muted-foreground">{hovered.month}</span>
            {series.length > 1 ? <span className="text-muted-foreground"> · {hovered.label}</span> : null}
            <span className="ml-1 font-medium tabular-nums">{full.format(Math.round(hovered.value))}</span>
          </div>
        ) : null}

        <div className="flex items-end gap-1" style={{ height }}>
          {months.map((mUnix, mi) => {
            const isSel = selected === mi;
            return (
              <button
                key={mUnix}
                type="button"
                aria-pressed={isSel}
                aria-label={`${monthYearFmt.format(new Date(mUnix * 1000))}: ${full.format(Math.round(totals[mi] ?? 0))}`}
                onClick={() => setSelected(isSel ? null : mi)}
                className={`flex flex-1 flex-col justify-end rounded-md px-0.5 transition-colors ${
                  isSel ? 'bg-muted' : 'hover:bg-muted/50'
                }`}
                style={{ height }}
              >
                {/* Bottom-up stack. Rendered top→bottom, so reverse the series for the DOM. */}
                <div className="flex w-full flex-col-reverse">
                  {series.map((s, si) => {
                    const v = s.values[mi] ?? 0;
                    if (v <= 0) return null;
                    const px = (v / max) * height;
                    const isTop = si === topSeriesByMonth[mi];
                    return (
                      <div
                        key={s.key}
                        onMouseEnter={() => setHover({ m: mi, s: si })}
                        onMouseLeave={() => setHover(null)}
                        style={{
                          height: Math.max(2, px),
                          background: colorOf(si),
                          marginTop: si === 0 ? 0 : 2,
                          borderTopLeftRadius: isTop ? 4 : 0,
                          borderTopRightRadius: isTop ? 4 : 0,
                        }}
                      />
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Month axis. */}
      <div className="flex gap-1">
        {months.map((mUnix, mi) => (
          <div
            key={mUnix}
            className={`flex-1 text-center text-[10px] ${
              selected === mi ? 'font-semibold text-foreground' : 'text-muted-foreground'
            }`}
          >
            {monthFmt.format(new Date(mUnix * 1000))}
          </div>
        ))}
      </div>

      {/* Legend — only for ≥2 series (a single series is named by the card title).
       *  Direct labels beside each swatch keep identity off colour alone. */}
      {series.length > 1 ? (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
          {series.map((s, si) => (
            <li key={s.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ background: colorOf(si) }}
                aria-hidden
              />
              <span className="truncate">{s.label}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Detail panel for the clicked month — the "more than a tooltip" view. */}
      {selected != null && selUnix != null ? (
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <div className="mb-2 text-sm font-medium capitalize">
            {monthYearFmt.format(new Date(selUnix * 1000))}
          </div>
          {renderDetail ? (
            renderDetail(selected)
          ) : (
            <ul className="space-y-1 text-sm">
              {[...series]
                .map((s, si) => ({ s, si, v: s.values[selected] ?? 0 }))
                .sort((a, b) => b.v - a.v)
                .map(({ s, si, v }) => (
                  <li key={s.key} className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
                      {series.length > 1 ? (
                        <span
                          className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                          style={{ background: colorOf(si) }}
                          aria-hidden
                        />
                      ) : null}
                      <span className="truncate">{s.label}</span>
                    </span>
                    <span className="tabular-nums">{full.format(Math.round(v))}</span>
                  </li>
                ))}
              <li className="flex items-center justify-between gap-3 border-t border-border pt-1 font-medium">
                <span>{t('subscription.total')}</span>
                <span className="tabular-nums">{full.format(Math.round(totals[selected] ?? 0))}</span>
              </li>
            </ul>
          )}
        </div>
      ) : (
        <div className="text-[10px] text-muted-foreground">
          {t('subscription.peak', { value: compact.format(max) })} · {t('subscription.clickHint')}
        </div>
      )}
    </div>
  );
}
